import React, { useState, useEffect, useMemo } from "react";
import { Calendar, Check, X, Filter } from "lucide-react";
import API from "../../api";

const LeaveManagement = () => {
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [modalPaidStatus, setModalPaidStatus] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalEntries, setTotalEntries] = useState(0);

  useEffect(() => {
    fetchLeaves();
  }, [currentPage, itemsPerPage, statusFilter]);

  const fetchLeaves = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        limit: itemsPerPage,
      });
      if (statusFilter !== "all") params.append("status", statusFilter);
      const res = await API.get(`/leaves?${params.toString()}`);
      setLeaves(res.data.leaves || []);
      setTotalEntries(res.data.pagination?.total || 0);
    } catch (err) {
      alert("Failed to load leaves.");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (id, status, remarks, isPaid) => {
    try {
      await API.put(`/leaves/${id}/status`, {
        status,
        admin_remarks: remarks,
        is_paid: isPaid,
      });
      await fetchLeaves();
      setSelectedLeave(null);
      setRemarks("");
      setModalPaidStatus(true);
    } catch {
      alert("Failed to update status.");
    }
  };

  // 🔹 Filter and Search
  const filteredLeaves = useMemo(() => {
    return leaves.filter((item) =>
      Object.values(item).some((val) =>
        String(val).toLowerCase().includes(search.toLowerCase())
      )
    );
  }, [leaves, search]);

  const totalPages = Math.ceil(totalEntries / itemsPerPage);

  const getStatusColor = (status) =>
    status === "approved"
      ? "bg-green-100 text-green-800"
      : status === "rejected"
      ? "bg-red-100 text-red-800"
      : "bg-yellow-100 text-yellow-800";

  const getLeaveTypeColor = (type) =>
    ({
      sick: "bg-red-100 text-red-800",
      vacation: "bg-blue-100 text-blue-800",
      personal: "bg-purple-100 text-purple-800",
      emergency: "bg-orange-100 text-orange-800",
    }[type] || "bg-gray-100 text-gray-800");

  const calculateDays = (s, e) =>
    Math.ceil((new Date(e) - new Date(s)) / (1000 * 60 * 60 * 24)) + 1;

  const approved = leaves.filter((l) => l.status === "approved").length;
  const rejected = leaves.filter((l) => l.status === "rejected").length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-center gap-3">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <Calendar className="w-5 h-5" /> Leave Management
        </h2>
        <div className="flex items-center gap-2">
          <input
            type="text"
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border rounded px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <Filter className="w-4 h-4 text-gray-500" />
          <select
            className="border rounded px-3 py-1 text-sm"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="all">All</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card title="Total" count={leaves.length} color="blue" icon={<Calendar />} />
        <Card title="Approved" count={approved} color="green" icon={<Check />} />
        <Card title="Rejected" count={rejected} color="red" icon={<X />} />
      </div>

      {/* Table */}
      <div className="bg-white shadow rounded-xl overflow-x-auto border border-gray-200">
        <table className="w-full table-auto text-sm">
          <thead className="bg-blue-600 text-white text-left">
            <tr>
              {[
                "Employee",
                "Leave Type",
                "Duration",
                "Days",
                "Applied Date",
                "Status",
                "Pay Type",
                "Actions",
              ].map((th) => (
                <th key={th} className="px-4 py-2 uppercase">{th}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="8" className="text-center py-6">Loading...</td></tr>
            ) : filteredLeaves.length > 0 ? (
              filteredLeaves.map((leave) => (
                <tr key={leave.id} className="border-b hover:bg-gray-50">
                  <td className="px-4 py-2">
                    <div className="font-medium">{leave.full_name}</div>
                    <div className="text-xs text-gray-500">
                      {leave.emp_id} • {leave.department}
                    </div>
                  </td>
                  <td className="px-4 py-2">
                    <span
                      className={`text-xs px-2 py-1 rounded-full ${getLeaveTypeColor(
                        leave.leave_type
                      )}`}
                    >
                      {leave.leave_type}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    {new Date(leave.start_date).toLocaleDateString()} -{" "}
                    {new Date(leave.end_date).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-2">{calculateDays(leave.start_date, leave.end_date)}</td>
                  <td className="px-4 py-2">
                    {new Date(leave.applied_date).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-2">
                    <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(leave.status)}`}>
                      {leave.status}
                    </span>
                    {leave.admin_remarks && (
                      <div className="text-xs text-gray-500">({leave.admin_remarks})</div>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    <span
                      className={`px-2 py-1 text-xs rounded-full ${
                        leave.is_paid
                          ? "bg-green-100 text-green-700"
                          : "bg-yellow-100 text-yellow-700"
                      }`}
                    >
                      {leave.is_paid ? "Paid" : "Unpaid"}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    {leave.status === "pending" && (
                      <>
                        <button
                          onClick={() => setSelectedLeave({ ...leave, action: "approve" })}
                          className="text-green-600 mr-2"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => setSelectedLeave({ ...leave, action: "reject" })}
                          className="text-red-600"
                        >
                          Reject
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="text-center py-6 text-gray-500">
                  No records found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex flex-wrap justify-center items-center gap-2 mt-4">
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 border rounded-md text-sm disabled:opacity-50"
          >
            Prev
          </button>

          {[...Array(totalPages)].map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentPage(i + 1)}
              className={`px-3 py-1 border rounded-md text-sm ${
                currentPage === i + 1
                  ? "bg-blue-600 text-white"
                  : "bg-white hover:bg-gray-100"
              }`}
            >
              {i + 1}
            </button>
          ))}

          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-1 border rounded-md text-sm disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}

      {/* Modal */}
      {selectedLeave && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-2">
              {selectedLeave.action === "approve" ? "Approve" : "Reject"} Leave
            </h3>
            <p className="text-sm text-gray-700 mb-2">
              {selectedLeave.full_name} ({selectedLeave.leave_type})<br />
              {new Date(selectedLeave.start_date).toLocaleDateString()} →{" "}
              {new Date(selectedLeave.end_date).toLocaleDateString()}
            </p>
            <textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Admin remarks..."
              className="w-full border rounded p-2 mb-3"
            />
            <select
              value={modalPaidStatus ? "1" : "0"}
              onChange={(e) => setModalPaidStatus(e.target.value === "1")}
              className="w-full border rounded p-2 mb-4"
            >
              <option value="1">Paid</option>
              <option value="0">Unpaid</option>
            </select>
            <div className="flex justify-end gap-3">
              <button
                className="text-gray-600"
                onClick={() => setSelectedLeave(null)}
              >
                Cancel
              </button>
              <button
                className={`px-4 py-2 rounded text-white ${
                  selectedLeave.action === "approve" ? "bg-green-600" : "bg-red-600"
                }`}
                onClick={() => {
                  if (selectedLeave.action === "reject" && !remarks.trim())
                    return alert("Remarks required for rejection");
                  handleStatusUpdate(
                    selectedLeave.id,
                    selectedLeave.action === "approve" ? "approved" : "rejected",
                    remarks,
                    modalPaidStatus
                  );
                }}
              >
                {selectedLeave.action === "approve" ? "Approve" : "Reject"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Card = ({ title, count, color, icon }) => (
  <div className="bg-white rounded-lg shadow p-4 flex items-center space-x-4">
    <div className={`w-10 h-10 flex items-center justify-center rounded-full bg-${color}-100 text-${color}-600`}>
      {icon}
    </div>
    <div>
      <p className="text-sm text-gray-500">{title}</p>
      <p className="text-xl font-bold text-gray-900">{count}</p>
    </div>
  </div>
);

export default LeaveManagement;
